import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUser, updateUser, createUser, getClients } from '../../services/admin_api';
import { sudoUser } from '../../services/auth';
import GlobalLayout from '../GlobalLayout';
import ChooseUserRoles from '../../components/ChooseUserRoles';

const UserEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNewUser = !id;
    const [user, setUser] = useState({
        email: '',
        firstname: '',
        lastname: '',
        apps_override: [],
        client_id: 0
    });
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(!isNewUser);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const clientsData = await getClients(1, 1000); // Fetch up to 1000 clients
                if (clientsData && Array.isArray(clientsData)) {
                    setClients(clientsData);
                } else if (clientsData && clientsData.data && Array.isArray(clientsData.data)) {
                    setClients(clientsData.data);
                }
            } catch (err) {
                console.error("Failed to fetch clients", err);
                // Handle error fetching clients, maybe set an error message
            }
        };

        fetchClients();
    }, []);

    useEffect(() => {
        if (!isNewUser) {
            const fetchUser = async () => {
                try {
                    const userData = await getUser(id);
                    if (userData) {
                        const userObj = userData.data || userData;
                        // S'assurer que les rôles sont un tableau
                        let apps_override = [];
                        if (userObj.apps_override) {
                            if (typeof userObj.apps_override === 'string') {
                                try {
                                    apps_override = JSON.parse(userObj.apps_override);
                                } catch (e) {
                                    console.error("Error parsing apps_override", e);
                                    apps_override = [];
                                }
                            } else if (Array.isArray(userObj.apps_override)) {
                                apps_override = userObj.apps_override;
                            }
                        }
                        setUser({ ...userObj, apps_override });
                    } else {
                        setError('User not found.');
                    }
                } catch (err) {
                    setError('Failed to fetch user data.');
                } finally {
                    setLoading(false);
                }
            };
            fetchUser();
        }
    }, [id, isNewUser]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUser(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleRoleChange = (newRoles) => {
        setUser(prevState => ({ ...prevState, apps_override: newRoles }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const userToSubmit = {
                ...user,
                client_id: parseInt(user.client_id, 10) || null, // Assurez-vous que client_id est un nombre
                apps_override: JSON.stringify(user.apps_override) // Stringify roles avant de les envoyer
            };

            console.log('Submitting user:', userToSubmit);
            if (isNewUser) {
                await createUser(userToSubmit);
            } else {
                await updateUser(id, userToSubmit);
            }
            navigate('/user'); // Redirige vers la liste des utilisateurs
        } catch (err) {
            setError(isNewUser ? 'Failed to create user.' : 'Failed to update user.');
        }
    };

    const handleSudo = async () => {
        try {
            await sudoUser(user.email);
            navigate('/');
        } catch (err) {
            setError('Failed to sudo user.');
        }
    };

    if (loading) {
        return <GlobalLayout><div>Loading...</div></GlobalLayout>;
    }

    if (error) {
        return <GlobalLayout><div>{error}</div></GlobalLayout>;
    }

    return (
        <GlobalLayout>
            <h1>{isNewUser ? 'Create User' : 'Edit User'}</h1>
            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="email">Email</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={user.email}
                        onChange={handleChange}
                        required
                        style={{ width: '100%', padding: '0.5rem' }}
                    />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="firstname">First Name</label>
                    <input
                        type="text"
                        id="firstname"
                        name="firstname"
                        value={user.firstname}
                        onChange={handleChange}
                        style={{ width: '100%', padding: '0.5rem' }}
                    />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="lastname">Last Name</label>
                    <input
                        type="text"
                        id="lastname"
                        name="lastname"
                        value={user.lastname}
                        onChange={handleChange}
                        style={{ width: '100%', padding: '0.5rem' }}
                    />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="client_id">Client</label>
                    <select
                        name="client_id"
                        id="client_id"
                        value={user.client_id || ""}
                        onChange={handleChange}
                        style={{ width: '100%', padding: '0.5rem' }}
                    >
                        <option value="">Select a client</option>
                        {clients.map((client) => (
                            <option key={client.id} value={client.id}>
                                {client.client_name}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                    <ChooseUserRoles
                        clientId={user.client_id}
                        selectedRoles={user.apps_override}
                        onRolesChange={handleRoleChange}
                    />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="submit" style={{ padding: '0.5rem 1rem' }}>{isNewUser ? 'Create User' : 'Save Changes'}</button>
                    {!isNewUser && (
                        <button type="button" onClick={handleSudo} style={{ padding: '0.5rem 1rem' }}>
                            Sudo
                        </button>
                    )}
                </div>
            </form>
        </GlobalLayout>
    );
};

export default UserEdit;
